import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { LANES, catColor, clock, dateClock, laneOf } from './eventItems';

const LANE_H = 34;
const AXIS_H = 26;
const LEFT = 132;
const RIGHT = 12;
const MIN = 60e3;
// midnight always gets the date (a parking over several days), long ranges label the days only
function tickLabel(t, withDate) {
    const d = new Date(t);
    const midnight = d.getHours() === 0 && d.getMinutes() === 0;
    if (midnight) return `${d.getMonth() + 1}.${d.getDate()}.`;
    return withDate && d.getMinutes() === 0 && d.getHours() % 6 === 0
        ? `${d.getHours()}h`
        : clock(t);
}

const STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 180, 360, 720, 1440].map((m) => m * MIN);

// one marker: diamond for failures, ring for warnings, dot otherwise
function Marker({ item, x, y, selected }) {
    const color = catColor(item.cat);
    const r = item.type === 'ID moved' ? 6 : 5;
    const common = { style: { cursor: 'pointer' }, opacity: item.context ? 0.55 : 1 };
    if (item.sev === 'fail') {
        const s = r + 1.5;
        return (
            <path
                d={`M${x},${y - s} L${x + s},${y} L${x},${y + s} L${x - s},${y} Z`}
                fill={color}
                stroke={selected ? '#000' : '#b71c1c'}
                strokeWidth={selected ? 2.5 : 1.5}
                {...common}
            />
        );
    }
    return (
        <circle
            cx={x}
            cy={y}
            r={selected ? r + 2 : r}
            fill={item.sev === 'warn' ? '#fff' : color}
            stroke={selected ? '#000' : color}
            strokeWidth={item.sev === 'warn' ? 3 : (selected && 2.5) || 1}
            {...common}
        />
    );
}

/**
 * Swimlane chart of timeline items. Drag horizontally to zoom in, double click to zoom out.
 * @param items timeline items (see eventItems.js)
 * @param from, to visible range (ms)
 * @param band optional {from, to, label} highlighted range (the parking itself)
 */
export default function EventChart({ items, from, to, band, selected, onSelect, onZoom }) {
    const theme = useTheme();
    const ref = useRef(null);
    const [width, setWidth] = useState(900);
    const [tip, setTip] = useState(null);
    const [drag, setDrag] = useState(null);

    useEffect(() => {
        const ro = new ResizeObserver(([entry]) =>
            setWidth(Math.max(360, Math.floor(entry.contentRect.width)))
        );
        ro.observe(ref.current);
        return () => ro.disconnect();
    }, []);

    const height = LANES.length * LANE_H + AXIS_H;
    const span = Math.max(MIN, to - from);
    const plotW = width - LEFT - RIGHT;
    const x = (t) => LEFT + ((t - from) / span) * plotW;
    const tAt = (px) => from + ((px - LEFT) / plotW) * span;

    const ticks = useMemo(() => {
        const step = STEPS.find((s) => (plotW / span) * s >= 80) || STEPS[STEPS.length - 1];
        const first = Math.ceil(from / step) * step;
        const out = [];
        for (let t = first; t <= to; t += step) out.push(t);
        return { list: out, withDate: step >= 360 * MIN || span > 20 * 3600e3 };
    }, [from, to, plotW, span]);

    const laneY = (cat) => LANES.indexOf(laneOf(cat)) * LANE_H + LANE_H / 2;
    const visible = items.filter((i) => i.ts >= from && i.ts <= to);

    const localX = (ev) => ev.clientX - ref.current.getBoundingClientRect().left;
    const onUp = () => {
        if (drag && Math.abs(drag.x1 - drag.x0) > 8 && onZoom) {
            const a = tAt(Math.min(drag.x0, drag.x1));
            const b = tAt(Math.max(drag.x0, drag.x1));
            onZoom(Math.max(from, a), Math.min(to, b));
        }
        setDrag(null);
    };

    return (
        <Box
            ref={ref}
            sx={{ position: 'relative', height, userSelect: 'none' }}
            onMouseLeave={() => {
                setTip(null);
                setDrag(null);
            }}
        >
            <svg
                width={width}
                height={height}
                style={{ display: 'block' }}
                onMouseDown={(ev) => {
                    const px = localX(ev);
                    if (px > LEFT) setDrag({ x0: px, x1: px });
                }}
                onMouseMove={(ev) => drag && setDrag({ ...drag, x1: localX(ev) })}
                onMouseUp={onUp}
                onDoubleClick={() => onZoom && onZoom(null, null)}
            >
                {LANES.map((l, i) => (
                    <g key={l.id}>
                        <rect
                            x={0}
                            y={i * LANE_H}
                            width={width}
                            height={LANE_H}
                            fill={i % 2 ? theme.palette.action.hover : 'transparent'}
                        />
                        <text
                            x={8}
                            y={i * LANE_H + LANE_H / 2 + 4}
                            fontSize="12"
                            fill={theme.palette.text.secondary}
                        >
                            {l.label}
                        </text>
                    </g>
                ))}
                {band && band.from < to && (band.to || to) > from && (
                    <g>
                        <rect
                            x={Math.max(LEFT, x(band.from))}
                            y={0}
                            width={Math.max(
                                2,
                                x(Math.min(to, band.to || to)) - Math.max(LEFT, x(band.from))
                            )}
                            height={LANES.length * LANE_H}
                            fill={theme.palette.primary.main}
                            opacity={0.07}
                        />
                        {[band.from, band.to]
                            .filter((t) => t && t >= from && t <= to)
                            .map((t) => (
                                <line
                                    key={t}
                                    x1={x(t)}
                                    x2={x(t)}
                                    y1={0}
                                    y2={LANES.length * LANE_H}
                                    stroke={theme.palette.primary.main}
                                    strokeDasharray="4 3"
                                    opacity={0.6}
                                />
                            ))}
                    </g>
                )}
                {ticks.list.map((t) => (
                    <g key={t}>
                        <line
                            x1={x(t)}
                            x2={x(t)}
                            y1={0}
                            y2={LANES.length * LANE_H}
                            stroke={
                                new Date(t).getHours() === 0 && new Date(t).getMinutes() === 0
                                    ? theme.palette.text.disabled
                                    : theme.palette.divider
                            }
                        />
                        <text
                            x={x(t)}
                            y={height - 8}
                            textAnchor="middle"
                            fontSize="11"
                            fill={theme.palette.text.secondary}
                        >
                            {tickLabel(t, ticks.withDate)}
                        </text>
                    </g>
                ))}
                {visible.map((i) => (
                    <g
                        key={i.k}
                        onClick={(ev) => {
                            ev.stopPropagation();
                            if (onSelect) onSelect(i.k);
                        }}
                        onMouseEnter={(ev) => setTip({ x: localX(ev), y: laneY(i.cat), item: i })}
                        onMouseLeave={() => setTip(null)}
                    >
                        <Marker item={i} x={x(i.ts)} y={laneY(i.cat)} selected={i.k === selected} />
                        {i.amount > 0 && i.cat === 'pay' && (
                            <text
                                x={x(i.ts)}
                                y={laneY(i.cat) - 9}
                                textAnchor="middle"
                                fontSize="10"
                                fill={theme.palette.text.primary}
                            >
                                {new Intl.NumberFormat('hu-HU').format(i.amount)}
                            </text>
                        )}
                    </g>
                ))}
                {drag && (
                    <rect
                        x={Math.min(drag.x0, drag.x1)}
                        y={0}
                        width={Math.abs(drag.x1 - drag.x0)}
                        height={LANES.length * LANE_H}
                        fill={theme.palette.primary.main}
                        opacity={0.15}
                    />
                )}
            </svg>
            {tip && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: Math.min(Math.max(0, tip.x - 130), width - 260),
                        top: tip.y + 14,
                        width: 260,
                        pointerEvents: 'none',
                        bgcolor: 'text.primary',
                        color: 'background.paper',
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                        zIndex: 2,
                    }}
                >
                    <Typography variant="caption" component="div">
                        {dateClock(tip.item.ts)} · {tip.item.gateLabel}
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
                        {tip.item.label}
                        {tip.item.amount ? ` · ${tip.item.amount} Ft` : ''}
                    </Typography>
                    {(tip.item.detail || tip.item.user) && (
                        <Typography variant="caption" component="div">
                            {[tip.item.detail, tip.item.user].filter(Boolean).join(' · ')}
                        </Typography>
                    )}
                    {tip.item.context && (
                        <Typography variant="caption" component="div">
                            (ugyanennél a kapunál, közben)
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}
